import { useEditor } from "@/utils/useEditor"
import React, { FC, PropsWithChildren, useEffect, useRef } from "react"
import { useObserveCanvasSize } from "@/utils/useObserveCanvasSize"
import { clamp } from "@/core/utils/math"
import { vec2 } from "gl-matrix"
import { useAtomValue, useSetAtom } from "jotai"
import { showingMaskingToolsOverlayAtom } from "@/store/editor/masks-state"
import { criticalFailureAtom } from "@/store/app-state"
import { viewportCanvasSettingsAtom } from "@/store/ui-views-state"
import { Animation, createAnimation } from "@/utils/animation"
import { useShortcuts } from "@/utils/useShortcuts"
import { actionShortcuts } from "@/shortcuts/shortcuts"
import { photosViewAtom } from "@/store/photo/photos-view-state"
import { unprojectPointerCoord } from "@/components/canvas-controls/canvas-controls-utils"
import { toggleAutoZoom } from "@/store/editor/editor-zoom"

export const Canvas: FC<PropsWithChildren> = (props) => {
	const editor = useEditor()
	const { observeRef } = useObserveCanvasSize()
	const setShowingMaskingOverlay = useSetAtom(showingMaskingToolsOverlayAtom)
	const setHasCriticalFailure = useSetAtom(criticalFailureAtom)
	const viewportCanvasSettings = useAtomValue(viewportCanvasSettingsAtom)
	const photosView = useAtomValue(photosViewAtom)
	const pointerOverImageRef = useRef(false)
	const appliedCursorRef = useRef("")
	const canvasRef = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		const listener = () => {
			setHasCriticalFailure(true)
		}

		editor.backend.canvas.addEventListener("webglcontextlost", listener)

		return () => editor.backend.canvas.removeEventListener("webglcontextlost", listener)
	}, [])

	useShortcuts({
		keys: [actionShortcuts.zoom],
		action: (event) => {
			if (event.repeat) {
				return
			}
			autoZoom()
		},
		trackingLabel: "zoom",
		preventDefault: true,
		disabled: !viewportCanvasSettings.isZoomableWithZ,
	})

	useShortcuts({
		keys: [actionShortcuts.space],
		action: (event) => {
			if (event.repeat) {
				return
			}
			autoZoom()
		},
		trackingLabel: "zoom",
		preventDefault: true,
		disabled: !viewportCanvasSettings.isZoomableWithSpace,
	})

	const isPointerOverImage = (pageX: number, pageY: number) => {
		if (photosView !== "focus") {
			return true
		}

		const coords = unprojectPointerCoord(pageX, pageY)

		// Non-finite coords can happen during resize; treat as outside the image.
		if (!Number.isFinite(coords[0]) || !Number.isFinite(coords[1])) {
			return false
		}

		return coords[0] >= 0 && coords[0] <= 1 && coords[1] >= 0 && coords[1] <= 1
	}

	const getCursorForState = (pointerOverImage: boolean) => {
		if (photosView === "focus" && !pointerOverImage) {
			return "default"
		}

		return editor.mainViewport.relativeZoom > 1 ? "grab" : "zoom-in"
	}

	// Update the canvas cursor directly to avoid rerenders on pointer move.
	const applyCursor = (pointerOverImage: boolean) => {
		const nextCursor = getCursorForState(pointerOverImage)
		if (appliedCursorRef.current === nextCursor) {
			return
		}

		appliedCursorRef.current = nextCursor
		if (canvasRef.current) {
			canvasRef.current.style.cursor = nextCursor
		}
	}

	const updatePointerOverImage = (pageX: number, pageY: number) => {
		const nextValue = isPointerOverImage(pageX, pageY)
		pointerOverImageRef.current = nextValue
		applyCursor(nextValue)
		return nextValue
	}

	const handleCanvasRef = (node: HTMLDivElement | null) => {
		observeRef(node)
		canvasRef.current = node
		if (node) {
			applyCursor(pointerOverImageRef.current)
		}
	}

	const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.ctrlKey || e.button !== 0) return //To prevent interference with right click

		e.preventDefault()

		const target = e.currentTarget
		const rect = target.getBoundingClientRect()

		const startPosX = editor.mainViewport.offset[0]
		const startPosY = editor.mainViewport.offset[1]
		const startMouseX = ((e.pageX - rect.x) / rect.width) * 2 - 1
		const startMouseY = ((e.pageY - rect.y) / rect.height) * 2 - 1

		const fitZoom = editor.mainViewport.croppedFitScale
		const currentZoom = editor.mainViewport.croppedImageScale

		const onPointerMove = (e: MouseEvent) => {
			if (!viewportCanvasSettings.isDraggableWithMouse) {
				return
			}

			const mouseX = ((e.pageX - rect.x) / rect.width) * 2 - 1
			const mouseY = ((e.pageY - rect.y) / rect.height) * 2 - 1

			const deltaX = mouseX - startMouseX
			const deltaY = mouseY - startMouseY

			const offsetX = startPosX + deltaX
			const offsetY = startPosY + deltaY

			if (currentZoom < fitZoom) {
				return
			}

			editor.setViewport({ offsetX, offsetY })
		}

		const onPointerUp = (e: MouseEvent) => {
			window.removeEventListener("pointermove", onPointerMove)
			window.removeEventListener("pointerup", onPointerUp)

			if (!viewportCanvasSettings.isDraggableWithMouse) {
				return
			}

			const mouseX = ((e.pageX - rect.x) / rect.width) * 2 - 1
			const mouseY = ((e.pageY - rect.y) / rect.height) * 2 - 1

			const deltaX = mouseX - startMouseX
			const deltaY = mouseY - startMouseY

			if (deltaX === 0 && deltaY === 0) {
				if (!updatePointerOverImage(e.pageX, e.pageY)) {
					return
				}

				const scale = editor.zoom
				const offset = getOffset(e, rect, scale)
				autoZoom(offset, 1)
			}
		}

		window.addEventListener("pointermove", onPointerMove)
		window.addEventListener("pointerup", onPointerUp)
	}

	let snapbackAnimation: Animation | null = null

	const handleWheel = (e: React.WheelEvent) => {
		if (viewportCanvasSettings.isZoomableWithTouchpadOnly && e.ctrlKey) {
			// Allow zooming with touchpad only
		} else if (!viewportCanvasSettings.isZoomableWithMouse) {
			return
		}

		if (!updatePointerOverImage(e.pageX, e.pageY)) {
			return
		}

		const isPinchZoom = e.nativeEvent.ctrlKey
		const target = e.currentTarget
		const rect = target.getBoundingClientRect()
		const delta = isPinchZoom ? e.deltaY / 100 : e.deltaY / 1000
		const fitZoom = editor.mainViewport.croppedFitScale
		const currentZoom = editor.mainViewport.croppedImageScale
		const deltaZoom = delta * currentZoom
		const minZoom = fitZoom / 2
		const zoom = clamp(currentZoom - deltaZoom, minZoom, 16)
		const scale = currentZoom / zoom

		const [offsetX, offsetY] = getOffset(e, rect, scale)

		if (zoom < fitZoom) {
			if (!snapbackAnimation) {
				snapbackAnimation = createAnimation(
					[offsetX, offsetY],
					[0, 0],
					(values, passthrough) => {
						editor.setViewport({
							offsetX: values[0],
							offsetY: values[1],
							zoom: passthrough[0],
						})
					},
				)
			}

			snapbackAnimation?.update([zoom])
		} else {
			snapbackAnimation?.cancel()
			snapbackAnimation = null

			editor.setViewport({
				offsetX,
				offsetY,
				zoom,
			})
		}

		applyCursor(pointerOverImageRef.current)
	}

	const getOffset = (e: MouseEvent | React.WheelEvent, rect: DOMRect, scale: number): vec2 => {
		if (editor.viewMode === "crop") {
			// Fix view to center in crop mode
			return [0, 0]
		}

		let mouseX = ((e.pageX - rect.x) / rect.width) * 2 - 1
		const mouseY = ((e.pageY - rect.y) / rect.height) * 2 - 1
		const x = editor.mainViewport.offset[0]
		const y = editor.mainViewport.offset[1]

		if (editor.viewMode === "compare") {
			mouseX = mouseX > 0 ? mouseX - 0.5 : mouseX + 0.5
		}

		// prettier-ignore
		return [
			(-mouseX + x) / scale - -mouseX,
			(-mouseY + y) / scale - -mouseY
		]
	}

	const autoZoom = (offset: vec2 = [0, 0], zoom = 1) => {
		toggleAutoZoom(editor, { offset, zoom })
		applyCursor(pointerOverImageRef.current)
	}

	return (
		<div
			className="relative flex flex-1"
			ref={handleCanvasRef}
			onPointerDown={handleMouseDown}
			onPointerMove={(e) => updatePointerOverImage(e.pageX, e.pageY)}
			onMouseEnter={(e) => {
				setShowingMaskingOverlay(true)
				updatePointerOverImage(e.pageX, e.pageY)
			}}
			onMouseLeave={() => {
				setShowingMaskingOverlay(false)
				pointerOverImageRef.current = false
				applyCursor(false)
			}}
			onWheel={handleWheel}
		>
			{props.children}
		</div>
	)
}
